import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  AutoAwesome,
  Delete,
  Download,
  Save,
  Tune,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import AppService from "../services/AppService";
import AdvancedGrowthService, {
  ADVANCED_FEATURE_DEFINITIONS,
  DEFAULT_ADVANCED_FEATURE_ID,
  DEFAULT_ADVANCED_INPUT,
} from "../services/AdvancedGrowthService";
import type { App } from "../types/app";
import type {
  AdvancedFeatureDefinition,
  AdvancedFeatureId,
  AdvancedLabInput,
  AdvancedLabRun,
} from "../types/advanced";

const MODELS = [
  { value: "", label: "Default (Gemini 2.5 Flash Lite)" },
  { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite" },
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
  { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
  { value: "gpt-4.1-mini", label: "OpenAI GPT-4.1 Mini" },
];

const CHANNEL_OPTIONS = [
  "App Store / Play Store",
  "SEO / Blog",
  "Social",
  "Email",
  "Push Notifications",
  "Paid Ads",
  "Partnerships",
  "Referral",
];

const PRIMARY_GOAL_OPTIONS = [
  "Increase paid conversion rate",
  "Improve onboarding activation",
  "Reduce CAC",
  "Increase D7 retention",
  "Grow MRR",
  "Improve app store conversion",
  "Launch a new acquisition channel",
];

const TARGET_AUDIENCE_OPTIONS = [
  "Early-stage founders",
  "Product managers",
  "Marketing teams",
  "Creators and solopreneurs",
  "Small business owners",
  "Operations teams",
  "Developers",
];

const BUDGET_RANGE_OPTIONS = [
  "< $5k / month",
  "$5k - $10k / month",
  "$10k - $25k / month",
  "$25k - $50k / month",
  "$50k+ / month",
  "No fixed budget",
];

const TIMELINE_OPTIONS = [
  "Next 30 days",
  "Next 60 days",
  "Next 90 days",
  "Next 6 months",
  "This quarter",
];

const CURRENT_METRICS_OPTIONS = [
  "CAC $42, Activation 21%, D7 retention 14%, MRR growth 5% MoM",
  "Free-to-paid conversion 2.1%, Trial-to-paid 38%, churn 4.8%",
  "App store CVR 3.2%, CTR 1.8%, install-to-signup 24%",
  "Organic traffic flat, signup conversion declining, retention stable",
];

const CONSTRAINT_OPTIONS = [
  "Small team with limited engineering capacity",
  "No paid media creative resources",
  "Short launch timeline",
  "Limited analytics instrumentation in place",
  "Strict compliance / approval requirements",
];

const NOTES_OPTIONS = [
  "Focus on highest-confidence wins first",
  "Assume no dedicated growth engineer",
  "Need an execution-ready weekly plan",
  "Prioritize experiments we can ship without a full redesign",
];

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return <div hidden={value !== index}>{value === index ? children : null}</div>;
}

function isFeatureId(value: string): value is AdvancedFeatureId {
  return ADVANCED_FEATURE_DEFINITIONS.some((item) => item.id === value);
}

function downloadText(content: string, fileName: string, mime: string) {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function SuggestedInputField({
  label,
  value,
  onChange,
  options,
  placeholder,
  multiline = false,
  minRows,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  multiline?: boolean;
  minRows?: number;
}) {
  return (
    <Autocomplete
      freeSolo
      options={options}
      value={value}
      onInputChange={(_, nextValue) => onChange(nextValue)}
      onChange={(_, nextValue) => onChange(typeof nextValue === "string" ? nextValue : "")}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          placeholder={placeholder}
          multiline={multiline}
          minRows={minRows}
        />
      )}
    />
  );
}

const AdvancedLab: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { featureId } = useParams<{ featureId?: string }>();
  const initialFeature = isFeatureId(featureId || "")
    ? (featureId as AdvancedFeatureId)
    : DEFAULT_ADVANCED_FEATURE_ID;

  const [tab, setTab] = useState(0);
  const [apps, setApps] = useState<App[]>([]);
  const [selectedAppId, setSelectedAppId] = useState("");
  const [selectedFeatureId, setSelectedFeatureId] =
    useState<AdvancedFeatureId>(initialFeature);
  const [model, setModel] = useState("");
  const [formInput, setFormInput] = useState<AdvancedLabInput>(DEFAULT_ADVANCED_INPUT);
  const [runs, setRuns] = useState<AdvancedLabRun[]>([]);
  const [currentRun, setCurrentRun] = useState<AdvancedLabRun | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const selectedFeature = useMemo<AdvancedFeatureDefinition>(
    () => AdvancedGrowthService.getFeatureDefinition(selectedFeatureId),
    [selectedFeatureId],
  );

  const selectedApp = useMemo(
    () => apps.find((item) => item.appId === selectedAppId) || null,
    [apps, selectedAppId],
  );

  const filteredRuns = useMemo(
    () => runs.filter((item) => item.featureId === selectedFeatureId),
    [runs, selectedFeatureId],
  );

  const loadApps = useCallback(async () => {
    if (!user?.email) return;
    const userApps = await AppService.getUserApps(user.email);
    setApps(userApps);
  }, [user?.email]);

  const loadRuns = useCallback(async () => {
    if (!user?.email || !selectedAppId) {
      setRuns([]);
      return;
    }
    const nextRuns = await AdvancedGrowthService.getAppRuns(user.email, selectedAppId);
    setRuns(nextRuns);
  }, [selectedAppId, user?.email]);

  useEffect(() => {
    loadApps().catch((loadError) => {
      console.error(loadError);
      setError("Failed to load registered apps.");
    });
  }, [loadApps]);

  useEffect(() => {
    loadRuns().catch((loadError) => {
      console.error(loadError);
      setError("Failed to load advanced runs.");
    });
  }, [loadRuns]);

  useEffect(() => {
    if (featureId && isFeatureId(featureId)) {
      setSelectedFeatureId(featureId);
      return;
    }
    if (featureId && !isFeatureId(featureId)) {
      navigate(`/advanced/${selectedFeatureId}`, { replace: true });
      return;
    }
    if (!featureId) {
      navigate(`/advanced/${selectedFeatureId}`, { replace: true });
    }
  }, [featureId, navigate, selectedFeatureId]);

  const setInput = <K extends keyof AdvancedLabInput>(
    key: K,
    value: AdvancedLabInput[K],
  ) => {
    setFormInput((prev) => ({ ...prev, [key]: value }));
  };

  const handleFeatureChange = (value: AdvancedFeatureId) => {
    setSelectedFeatureId(value);
    setCurrentRun(null);
    navigate(`/advanced/${value}`);
  };

  const handleGenerate = async () => {
    if (!user?.email || !selectedApp) {
      setError("Select a registered app first.");
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const generated = await AdvancedGrowthService.generatePlan({
        userId: user.email,
        app: selectedApp,
        featureId: selectedFeatureId,
        formInput,
        model: model || undefined,
      });
      setCurrentRun(generated);
      setTab(1);
      setSuccess(`${selectedFeature.label} plan generated.`);
    } catch (generationError: any) {
      console.error(generationError);
      setError(generationError?.message || "Failed to generate advanced plan.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentRun) return;
    setSaving(true);
    setError(null);
    try {
      await AdvancedGrowthService.saveRun(currentRun);
      await loadRuns();
      setSuccess("Plan saved.");
    } catch (saveError: any) {
      console.error(saveError);
      setError(saveError?.message || "Failed to save plan.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRun = async (run: AdvancedLabRun) => {
    if (!user?.email) return;
    if (!window.confirm("Delete this advanced run?")) return;
    try {
      await AdvancedGrowthService.deleteRun(user.email, run.runId);
      await loadRuns();
      if (currentRun?.runId === run.runId) {
        setCurrentRun(null);
      }
      setSuccess("Run deleted.");
    } catch (deleteError: any) {
      console.error(deleteError);
      setError(deleteError?.message || "Failed to delete run.");
    }
  };

  const exportRun = (run: AdvancedLabRun, format: "json" | "md") => {
    const safeFeature = run.featureId.replace(/[^a-zA-Z0-9-_]/g, "_");
    const safeApp = run.appName.replace(/[^a-zA-Z0-9-_]/g, "_");
    if (format === "json") {
      downloadText(
        AdvancedGrowthService.exportAsJson(run),
        `${safeApp}-${safeFeature}-advanced.json`,
        "application/json",
      );
      return;
    }
    downloadText(
      AdvancedGrowthService.exportAsMarkdown(run),
      `${safeApp}-${safeFeature}-advanced.md`,
      "text/markdown",
    );
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
        Advanced Growth Lab
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Execute advanced strategy modules end-to-end with app context, measurable plans, and saved run history.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 2 }}
      >
        <Tab label="Planner" />
        <Tab label="Current Plan" />
        <Tab label="Saved Plans" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <Paper sx={{ p: 2.5, mb: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Registered App"
                value={selectedAppId}
                onChange={(event) => setSelectedAppId(event.target.value)}
              >
                <MenuItem value="">
                  <em>Select a registered app</em>
                </MenuItem>
                {apps.map((app) => (
                  <MenuItem key={app.appId} value={app.appId}>
                    {app.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                select
                fullWidth
                label="Advanced Feature"
                value={selectedFeatureId}
                onChange={(event) =>
                  handleFeatureChange(event.target.value as AdvancedFeatureId)
                }
              >
                {ADVANCED_FEATURE_DEFINITIONS.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Model (Optional)"
                value={model}
                onChange={(event) => setModel(event.target.value)}
              >
                {MODELS.map((option) => (
                  <MenuItem key={option.value || "default"} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderColor: "primary.light",
                  bgcolor: "primary.50",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Tune fontSize="small" color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {selectedFeature.label}
                  </Typography>
                  <Chip size="small" label={selectedFeature.focusArea} color="primary" />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {selectedFeature.description}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {selectedFeature.relatedSkills.map((skill) => (
                    <Chip key={skill} size="small" label={skill} variant="outlined" />
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <SuggestedInputField
                label="Primary Goal"
                value={formInput.primaryGoal}
                onChange={(value) => setInput("primaryGoal", value)}
                options={PRIMARY_GOAL_OPTIONS}
                placeholder="e.g. Increase subscription conversion from 2.1% to 3.5%"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SuggestedInputField
                label="Target Audience"
                value={formInput.targetAudience}
                onChange={(value) => setInput("targetAudience", value)}
                options={TARGET_AUDIENCE_OPTIONS}
                placeholder="e.g. Early-stage founders and product teams"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SuggestedInputField
                label="Budget Range"
                value={formInput.budgetRange}
                onChange={(value) => setInput("budgetRange", value)}
                options={BUDGET_RANGE_OPTIONS}
                placeholder="e.g. $10k-20k / month"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SuggestedInputField
                label="Timeline"
                value={formInput.timeline}
                onChange={(value) => setInput("timeline", value)}
                options={TIMELINE_OPTIONS}
                placeholder="e.g. Next 12 weeks"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                SelectProps={{ multiple: true }}
                label="Channels"
                value={formInput.channels}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setInput(
                    "channels",
                    Array.isArray(nextValue)
                      ? nextValue
                      : String(nextValue || "")
                          .split(",")
                          .map((item) => item.trim())
                          .filter(Boolean),
                  );
                }}
              >
                {CHANNEL_OPTIONS.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <SuggestedInputField
                label="Current Metrics"
                value={formInput.currentMetrics}
                onChange={(value) => setInput("currentMetrics", value)}
                options={CURRENT_METRICS_OPTIONS}
                multiline
                minRows={2}
                placeholder="e.g. CAC $42, Activation 21%, D7 retention 14%, MRR growth 5% MoM"
              />
            </Grid>
            <Grid item xs={12}>
              <SuggestedInputField
                label="Constraints"
                value={formInput.constraints}
                onChange={(value) => setInput("constraints", value)}
                options={CONSTRAINT_OPTIONS}
                multiline
                minRows={2}
                placeholder="e.g. Small team (2 engineers), no paid social video editing resource"
              />
            </Grid>
            <Grid item xs={12}>
              <SuggestedInputField
                label="Additional Notes"
                value={formInput.notes}
                onChange={(value) => setInput("notes", value)}
                options={NOTES_OPTIONS}
                multiline
                minRows={3}
                placeholder="Any specific context, assumptions, or mandatory requirements..."
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<AutoAwesome />}
                onClick={handleGenerate}
                disabled={loading || !selectedApp}
              >
                {loading ? "Generating..." : "Generate Advanced Plan"}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, mt: 2, bgcolor: "action.hover", border: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Quick Feature Switching
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {ADVANCED_FEATURE_DEFINITIONS.map((item) => (
              <Chip
                key={item.id}
                label={item.label}
                clickable
                color={item.id === selectedFeatureId ? "primary" : "default"}
                onClick={() => handleFeatureChange(item.id)}
                variant={item.id === selectedFeatureId ? "filled" : "outlined"}
              />
            ))}
          </Box>
        </Paper>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        {!currentRun ? (
          <Alert severity="info">Generate a plan first or open one from Saved Plans.</Alert>
        ) : (
          <Box>
            <Paper sx={{ p: 2.5, mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {currentRun.featureLabel}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    App: {currentRun.appName} | Model: {currentRun.modelUsed}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={() => exportRun(currentRun, "md")}
                  >
                    Markdown
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={() => exportRun(currentRun, "json")}
                  >
                    JSON
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save Plan"}
                  </Button>
                </Box>
              </Box>
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper sx={{ p: 2.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Summary
                  </Typography>
                  <Typography variant="body1">{currentRun.output.summary}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2.5, height: "100%" }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Diagnosis
                  </Typography>
                  {currentRun.output.diagnosis.length === 0 ? (
                    <Typography color="text.secondary">No diagnosis generated.</Typography>
                  ) : (
                    currentRun.output.diagnosis.map((item, index) => (
                      <Typography key={`diag-${index}`} variant="body2" sx={{ mb: 0.8 }}>
                        • {item}
                      </Typography>
                    ))
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2.5, height: "100%" }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Strategy
                  </Typography>
                  {currentRun.output.strategy.length === 0 ? (
                    <Typography color="text.secondary">No strategy generated.</Typography>
                  ) : (
                    currentRun.output.strategy.map((item, index) => (
                      <Typography key={`strat-${index}`} variant="body2" sx={{ mb: 0.8 }}>
                        • {item}
                      </Typography>
                    ))
                  )}
                </Paper>
              </Grid>
            </Grid>

            <Paper sx={{ p: 2.5, mt: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                Experiments
              </Typography>
              <Grid container spacing={2}>
                {currentRun.output.experiments.map((item, index) => (
                  <Grid item xs={12} md={6} key={`${item.name}-${index}`}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {item.name}
                          </Typography>
                          <Chip
                            label={item.priority.toUpperCase()}
                            size="small"
                            color={
                              item.priority === "high"
                                ? "error"
                                : item.priority === "medium"
                                ? "warning"
                                : "success"
                            }
                          />
                        </Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Hypothesis:</strong> {item.hypothesis}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Primary KPI:</strong> {item.primaryKpi}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Guardrail KPI:</strong> {item.guardrailKpi}
                        </Typography>
                        {item.implementationSteps.map((step, stepIndex) => (
                          <Typography key={`${index}-${stepIndex}`} variant="body2">
                            {stepIndex + 1}. {step}
                          </Typography>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={7}>
                <Paper sx={{ p: 2.5, height: "100%" }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Execution Plan
                  </Typography>
                  {currentRun.output.executionPlan.map((step, index) => (
                    <Box key={`${step.weekLabel}-${index}`} sx={{ mb: 1.8 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {step.weekLabel}: {step.objective}
                      </Typography>
                      {step.tasks.map((task, taskIndex) => (
                        <Typography key={`${index}-${taskIndex}`} variant="body2">
                          • {task}
                        </Typography>
                      ))}
                    </Box>
                  ))}
                </Paper>
              </Grid>
              <Grid item xs={12} md={5}>
                <Paper sx={{ p: 2.5, mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Tracking Plan
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Primary Metrics
                  </Typography>
                  {currentRun.output.trackingPlan.primaryMetrics.map((item, index) => (
                    <Typography key={`pm-${index}`} variant="body2">
                      • {item}
                    </Typography>
                  ))}
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
                    Secondary Metrics
                  </Typography>
                  {currentRun.output.trackingPlan.secondaryMetrics.map((item, index) => (
                    <Typography key={`sm-${index}`} variant="body2">
                      • {item}
                    </Typography>
                  ))}
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
                    Event Tracking
                  </Typography>
                  {currentRun.output.trackingPlan.eventTracking.map((item, index) => (
                    <Typography key={`ev-${index}`} variant="body2">
                      • {item}
                    </Typography>
                  ))}
                </Paper>
                <Paper sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Risks
                  </Typography>
                  {currentRun.output.risks.map((item, index) => (
                    <Typography key={`risk-${index}`} variant="body2">
                      • {item}
                    </Typography>
                  ))}
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
                    Next Actions
                  </Typography>
                  {currentRun.output.nextActions.map((item, index) => (
                    <Typography key={`next-${index}`} variant="body2">
                      • {item}
                    </Typography>
                  ))}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tab} index={2}>
        {!selectedApp ? (
          <Alert severity="info">Select an app to view saved advanced plans.</Alert>
        ) : filteredRuns.length === 0 ? (
          <Alert severity="info">No saved plans for this advanced feature yet.</Alert>
        ) : (
          <Grid container spacing={2}>
            {filteredRuns.map((run) => (
              <Grid item xs={12} md={6} key={run.runId}>
                <Card variant="outlined">
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant="h6">{run.featureLabel}</Typography>
                      <Chip size="small" label={run.modelUsed} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Saved: {(run.savedAt || run.createdAt).toLocaleString()}
                    </Typography>
                    <Typography variant="body2">{run.output.summary}</Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      onClick={() => {
                        setCurrentRun(run);
                        setTab(1);
                      }}
                    >
                      Open
                    </Button>
                    <Button
                      size="small"
                      startIcon={<Download />}
                      onClick={() => exportRun(run, "md")}
                    >
                      Markdown
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => handleDeleteRun(run)}
                    >
                      Delete
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>
    </Box>
  );
};

export default AdvancedLab;
